/**
 * TileService - Service class for managing map tile operations.
 *
 * Provides utilities for:
 * - Tile URL generation
 * - Tile caching strategy
 * - Prefetching logic
 */

import {
  buildTileUrl,
  getTileKey,
  getNeighborTiles,
  tileToLatLngBounds,
} from '../utils/tileCalculations'
import { getTileUrlTemplate, DEFAULT_TILE_LAYER, TILE_SERVERS } from '../constants'
import type { TileCoordinate, TileBounds, GlobeTile, TileLayerType } from '../types'

/**
 * Tile service configuration.
 */
export interface TileServiceConfig {
  /** Tile layer type (determines the URL template) */
  layer: TileLayerType
  /** Number of servers for load balancing */
  serverCount: number
}

/**
 * Default configuration for the tile service.
 */
const DEFAULT_CONFIG: TileServiceConfig = {
  layer: DEFAULT_TILE_LAYER,
  serverCount: TILE_SERVERS,
}

/**
 * Service for managing map tile operations.
 *
 * This is a stateless service that provides utility functions
 * for working with map tiles. For stateful tile management
 * (loading, caching), use the useTileLoader hook.
 */
export class TileService {
  private config: TileServiceConfig

  constructor(config: Partial<TileServiceConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config }
  }

  /**
   * Get the URL for a tile.
   */
  getTileUrl(coord: TileCoordinate): string {
    const urlTemplate = getTileUrlTemplate(this.config.layer)
    return buildTileUrl(urlTemplate, coord, this.config.serverCount)
  }

  /**
   * Get the unique key for a tile (for caching).
   */
  getTileKey(coord: TileCoordinate): string {
    return getTileKey(coord)
  }

  /**
   * Get the geographic bounds of a tile.
   */
  getTileBounds(coord: TileCoordinate): TileBounds {
    return tileToLatLngBounds(coord)
  }

  /**
   * Get tiles that should be prefetched based on current view.
   *
   * Returns neighboring tiles that aren't in the current visible set.
   */
  getTilesToPrefetch(
    visibleTiles: TileCoordinate[],
    maxPrefetch: number = 8
  ): TileCoordinate[] {
    const visibleKeys = new Set(visibleTiles.map((t) => getTileKey(t)))
    const prefetchTiles: TileCoordinate[] = []
    const addedKeys = new Set<string>()

    // Get neighbors of visible tiles
    for (const tile of visibleTiles) {
      const neighbors = getNeighborTiles(tile)
      for (const neighbor of neighbors) {
        const key = getTileKey(neighbor)
        if (!visibleKeys.has(key) && !addedKeys.has(key)) {
          prefetchTiles.push(neighbor)
          addedKeys.add(key)

          if (prefetchTiles.length >= maxPrefetch) {
            return prefetchTiles
          }
        }
      }
    }

    return prefetchTiles
  }

  /**
   * Create a GlobeTile object from a coordinate.
   */
  createTile(coord: TileCoordinate): GlobeTile {
    return {
      coord,
      bounds: this.getTileBounds(coord),
      status: 'loading',
      url: this.getTileUrl(coord),
    }
  }

  /**
   * Check if a tile is valid (within zoom bounds).
   */
  isValidTile(coord: TileCoordinate): boolean {
    const maxTile = Math.pow(2, coord.z)
    return (
      coord.z >= 0 &&
      coord.z <= 22 &&
      coord.x >= 0 &&
      coord.x < maxTile &&
      coord.y >= 0 &&
      coord.y < maxTile
    )
  }

  /**
   * Get the parent tile at a lower zoom level.
   */
  getParentTile(coord: TileCoordinate): TileCoordinate | null {
    if (coord.z <= 0) return null

    return {
      x: Math.floor(coord.x / 2),
      y: Math.floor(coord.y / 2),
      z: coord.z - 1,
    }
  }

  /**
   * Get the child tiles at a higher zoom level.
   */
  getChildTiles(coord: TileCoordinate): TileCoordinate[] {
    if (coord.z >= 22) return []

    const baseX = coord.x * 2
    const baseY = coord.y * 2
    const childZ = coord.z + 1

    return [
      { x: baseX, y: baseY, z: childZ },
      { x: baseX + 1, y: baseY, z: childZ },
      { x: baseX, y: baseY + 1, z: childZ },
      { x: baseX + 1, y: baseY + 1, z: childZ },
    ]
  }
}

/**
 * Singleton instance of the tile service.
 */
export const tileService = new TileService()
